import { Option, Select } from '@d-edge/overnight-hotelier-react'
import { useState } from 'react'
import { usePriceRecommendationMetrics } from '@/api/queries'
import type { PriceRecommendationMetrics } from '@/api/types'
import { THRESHOLDS } from '@/features/attention/attention'
import { KpiCard, type Stat } from '@/features/cockpit/KpiCard'
import { compactNumber, percent } from '@/lib/format'
import styles from './PriceRecommendationKpi.module.css'

const WINDOWS = [24, 72, 168]

/** Health probe of the recommendation API; unknown when no probe URL is configured. */
function healthTile(healthy: boolean | null | undefined): Stat {
  if (healthy === true) return { label: 'API health', value: 'Healthy', tone: 'ok' }
  if (healthy === false) return { label: 'API health', value: 'Down', tone: 'danger' }
  return { label: 'API health', value: healthy === null ? 'Unknown' : '–' }
}

/** The card is deliberately basic: volume (headline), the health probe and API errors. Business metrics stay out of the cockpit. */
function statsFor(data: PriceRecommendationMetrics | undefined): Stat[] {
  const m = data?.metricsAvailable ? data : undefined
  return [
    healthTile(data?.healthy),
    {
      label: 'API errors',
      value: percent(m?.errorRate, 2),
      tone: m ? (m.errorRate > THRESHOLDS.priceApiErrorCritical ? 'danger' : m.errorRate > THRESHOLDS.priceApiErrorWarn ? 'warn' : 'ok') : undefined,
    },
  ]
}

/** Recommendations and hotels served by the price recommendation API (BigQuery), its error rate and health probe. */
export function PriceRecommendationKpi({ className }: { className?: string }) {
  const [hours, setHours] = useState(24)
  const { data, isLoading, error } = usePriceRecommendationMetrics(hours)
  const m = data?.metricsAvailable ? data : undefined

  const actions = (
    <Select value={String(hours)} onChange={(value) => setHours(Number(value))} aria-label='Time window' className={styles.select}>
      {WINDOWS.map((h) => (
        <Option key={h} value={String(h)} label={h >= 48 ? `${h / 24} days` : `${h} hours`} />
      ))}
    </Select>
  )

  const headline = m
    ? [
        { value: compactNumber(m.recommendationsGenerated), label: 'recommendations' },
        { value: m.activeHotels, label: 'hotels' },
      ]
    : [{ value: '–', label: error ? 'metrics unavailable' : isLoading ? '' : 'business metrics not configured' }]

  return <KpiCard className={className} title="Price recommendation" loading={isLoading} actions={actions} headline={headline} stats={statsFor(data)} />
}
